<template>
    <div
        ref="rootRef"
        class="select-lookup"
        :class="{ 'select-lookup--open': isOpen }"
        @keydown.esc="closeDropdown"
    >
        <label
            v-if="label"
            class="lookup-dropdown__label select-lookup__label"
            @click.prevent="toggleDropdown"
        >
            {{ label }}
        </label>

        <!-- Input row + dropdown anchored together so menu opens directly below input -->
        <div class="select-lookup__control">
            <div class="select-lookup__input-row">
                <input
                    ref="inputRef"
                    type="text"
                    class="lookup-dropdown__input select-lookup__search-input"
                    :placeholder="computedPlaceholder"
                    :value="searchTerm"
                    @input="onInput"
                    @click="onInputClick"
                    @keydown.enter.prevent="onEnterKey"
                />
                <button
                    v-if="showClearBtn"
                    type="button"
                    class="select-lookup__clear-btn"
                    title="Șterge selecția"
                    @mousedown.prevent="clearAll"
                >
                    ×
                </button>
            </div>

            <!-- Dropdown menu — positioned absolute relative to .select-lookup__control -->
            <div v-if="isOpen" class="lookup-dropdown__menu select-lookup__menu">
                <div v-if="isLoading" class="lookup-dropdown__state">Se încarcă...</div>
                <template v-else>
                    <button
                        v-for="option in sortedOptions"
                        :key="option.id"
                        type="button"
                        class="select-lookup__option"
                        :class="{ 'select-lookup__option--selected': isSelected(option.id) }"
                        @mousedown.prevent="toggleOption(option)"
                    >
                        <span v-if="multiple" class="select-lookup__option-check" aria-hidden="true">
                            <i v-if="isSelected(option.id)" class="mdi mdi-check" aria-hidden="true"></i>
                        </span>
                        {{ option.name }}
                    </button>
                    <div v-if="sortedOptions.length === 0" class="lookup-dropdown__state">
                        Nu există rezultate.
                    </div>
                </template>
            </div>
        </div>

        <!-- Multiple: selected tags shown as blue text below the control (only when 2+) -->
        <div v-if="multiple && selectedOptions.length > 1" class="select-lookup__tags">
            {{ selectedOptions.map((o) => o.name).join(', ') }}
        </div>

        <div v-if="errorMessage" class="lookup-dropdown__error">{{ errorMessage }}</div>
    </div>
</template>

<script>
export default {
    name: 'SelectSearchLookupDropdown',

    props: {
        modelValue: {
            type: [Number, String, Array, null],
            default: null,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        endpoint: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Caută...',
        },
        minimumSearchLength: {
            type: Number,
            default: 1,
        },
    },

    emits: ['update:modelValue', 'selected'],

    data() {
        return {
            searchTerm: '',
            options: [],
            selectedOptions: [],
            isOpen: false,
            isLoading: false,
            errorMessage: '',
            debounceTimer: null,
        };
    },

    computed: {
        trimmedSearchTerm() {
            return this.searchTerm.trim();
        },

        computedPlaceholder() {
            if (this.multiple && this.selectedOptions.length > 1) {
                return `${this.selectedOptions.length} selectate`;
            }
            return this.placeholder;
        },

        showClearBtn() {
            return this.selectedOptions.length === 1;
        },

        sortedOptions() {
            if (!this.multiple || this.selectedOptions.length === 0) {
                return this.options;
            }
            const selectedIds = new Set(this.selectedOptions.map((o) => String(o.id)));
            const selected = this.options.filter((o) => selectedIds.has(String(o.id)));
            const unselected = this.options.filter((o) => !selectedIds.has(String(o.id)));
            return [...selected, ...unselected];
        },
    },

    watch: {
        isOpen(val) {
            if (val) {
                this.$nextTick(() => {
                    document.addEventListener('mousedown', this._clickOutsideHandler);
                    if (this.$refs.inputRef) {
                        this.$refs.inputRef.focus();
                    }
                });
            } else {
                document.removeEventListener('mousedown', this._clickOutsideHandler);
            }
        },

        modelValue(newVal) {
            const newIds = (Array.isArray(newVal) ? newVal : (newVal != null ? [newVal] : []))
                .map(String).sort().join(',');
            const currentIds = this.selectedOptions.map((o) => String(o.id)).sort().join(',');
            if (newIds === currentIds) return;

            if (!newVal || (Array.isArray(newVal) && newVal.length === 0)) {
                this.selectedOptions = [];
                this.searchTerm = '';
                return;
            }

            this.loadInitialValues();
        },
    },

    async mounted() {
        this._clickOutsideHandler = (e) => {
            if (this.$refs.rootRef && !this.$refs.rootRef.contains(e.target)) {
                this.closeDropdown();
            }
        };
        await this.loadInitialValues();
        await this.fetchOptions('');
    },

    beforeUnmount() {
        document.removeEventListener('mousedown', this._clickOutsideHandler);
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },

    methods: {
        async loadInitialValues() {
            let ids;
            if (this.multiple) {
                ids = Array.isArray(this.modelValue) ? this.modelValue : [];
            } else {
                ids = this.modelValue != null ? [this.modelValue] : [];
            }

            if (ids.length === 0) return;

            this.isLoading = true;
            try {
                const response = await window.axios.get(this.endpoint, {
                    params: { ids },
                });
                const data = Array.isArray(response.data?.data) ? response.data.data : [];
                const idSet = new Set(ids.map(String));
                this.selectedOptions = data.filter((item) => idSet.has(String(item.id)));

                if (!this.multiple && this.selectedOptions.length > 0) {
                    this.searchTerm = this.selectedOptions[0].name;
                } else if (this.multiple && this.selectedOptions.length === 1) {
                    this.searchTerm = this.selectedOptions[0].name;
                }
            } catch {
                // Silently fail — no initial display
            } finally {
                this.isLoading = false;
            }
        },

        async fetchOptions(query) {
            if (query.length > 0 && query.length < this.minimumSearchLength) {
                this.options = [];
                return;
            }

            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await window.axios.get(this.endpoint, {
                    params: { q: query },
                });
                this.options = Array.isArray(response.data?.data) ? response.data.data : [];
            } catch {
                this.errorMessage = 'Nu am putut încărca datele.';
                this.options = [];
            } finally {
                this.isLoading = false;
            }
        },

        onInput(event) {
            this.searchTerm = event.target.value;
            this.isOpen = true;

            if (!this.multiple && this.selectedOptions.length > 0) {
                this.selectedOptions = [];
                this.emitValue();
            }

            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.fetchOptions(this.trimmedSearchTerm);
            }, 250);
        },

        onInputClick() {
            if (this.multiple) {
                this.toggleDropdown();
            } else {
                this.openDropdown();
            }
        },

        openDropdown() {
            if (!this.isOpen) {
                this.isOpen = true;
                if (this.options.length === 0) {
                    this.fetchOptions('');
                }
            }
        },

        closeDropdown() {
            this.isOpen = false;
            if (!this.multiple && this.selectedOptions.length === 0) {
                this.searchTerm = '';
            }
        },

        toggleDropdown() {
            if (this.isOpen) {
                this.closeDropdown();
            } else {
                this.isOpen = true;
                if (this.options.length === 0) {
                    this.fetchOptions('');
                }
            }
        },

        isSelected(id) {
            return this.selectedOptions.some((o) => String(o.id) === String(id));
        },

        toggleOption(option) {
            if (this.multiple) {
                if (this.isSelected(option.id)) {
                    this.selectedOptions = this.selectedOptions.filter(
                        (o) => String(o.id) !== String(option.id)
                    );
                } else {
                    this.selectedOptions = [...this.selectedOptions, option];
                }

                if (this.selectedOptions.length === 1) {
                    this.searchTerm = this.selectedOptions[0].name;
                } else {
                    this.searchTerm = '';
                }

                this.emitValue();
            } else {
                this.selectedOptions = [option];
                this.searchTerm = option.name;
                this.emitValue();
                this.$emit('selected', option);
                this.closeDropdown();
            }
        },

        clearAll() {
            this.selectedOptions = [];
            this.searchTerm = '';
            this.emitValue();
            this.closeDropdown();
        },

        emitValue() {
            if (this.multiple) {
                this.$emit('update:modelValue', this.selectedOptions.map((o) => o.id));
            } else {
                this.$emit('update:modelValue', this.selectedOptions[0]?.id ?? null);
            }
        },

        onEnterKey() {
            const firstUnselected = this.sortedOptions.find((o) => !this.isSelected(o.id));
            if (firstUnselected) {
                this.toggleOption(firstUnselected);
            }
        },
    },
};
</script>
